import { EventResultItem } from '@common/types/search';
import { Flag, VictronImage } from '@components/display/images';
import { HighlightText } from '@components/display/richTexts';
import { useGetLanguageSpokenOptionsQuery } from '@event/services';
import { getEventTypeShortLabel } from '@event/utils';
import LanguageIcon from '@mui/icons-material/Language';
import { alpha, Avatar, Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

type EventResultCardProps = {
    loading?: boolean;
    data?: EventResultItem;
    onMouseEnter?: () => void;
    term: string;
};

export const EventResultCard = ({ loading, data, onMouseEnter, term }: EventResultCardProps) => {
    const { data: languageOptions, isLoading: areLanguageOptionsLoading } = useGetLanguageSpokenOptionsQuery();

    const url = typeof data?.url === 'string' ? { href: data.url, target: undefined } : data?.url;
    
    const Image = (
        <Box
            position="relative"
            width="100%"
            className="aspect-[calc(16/9)] transition-transform duration-300 ease-out-quad group-hover:scale-105"
        >
            <VictronImage
                src={data?.banner_image}
                alt={`Banner of ${data?.name}`}
                className={'h-full w-full rounded-md object-cover'}
            />
            <Flag flag={data?.country_code} className="absolute top-[6px] left-[5px] rounded w-[35px] h-[23px]" />
        </Box>
    );

    const CardContent = (
        <Stack
            direction="column"
            bgcolor={(theme) => theme.palette.white.main}
            borderRadius="8px"
            border={1}
            borderColor={alpha('#000', 0.16)}
            p="15px"
            spacing={{ xs: '10px', md: '19.5px' }}
            onMouseEnter={() => onMouseEnter?.()}
            height="100%"
            width="100%"
            className="group"
        >
            {loading ? (
                <Skeleton variant="rounded" width="100%">
                    {Image}
                </Skeleton>
            ) : (
                Image
            )}
            <Stack
                direction="column"
                spacing="8px"
                flexGrow={1}
                minWidth={0}
                overflow="hidden"
                justifyContent="space-between"
            >
                {loading ? (
                    <>
                        <Skeleton variant="text" width="100%" height={26} />
                        <Stack direction="row" gap="10px" height={19}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                        </Stack>
                    </>
                ) : (
                    <>
                        <div className="line-clamp-2">
                            <HighlightText text={data?.name ?? ''} term={term} />
                        </div>
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap="10px">
                            {data?.language_code && (
                                <Stack direction="row" spacing="3px" alignItems="center">
                                    <Avatar
                                        src={`/static/flags/${data.language_code}.svg`}
                                        className="w-[17px] h-[17px]"
                                    />
                                    <Typography fontSize={16} color="neutral-550.main" lineHeight="24px" noWrap>
                                        {
                                            languageOptions?.find(
                                                (langOption) => langOption.code === data.language_code
                                            )?.name
                                        }
                                    </Typography>
                                </Stack>
                            )}
                            {data?.type && (
                                <Stack direction="row" spacing="3px" alignItems="center">
                                    <LanguageIcon
                                        sx={{ fill: (theme) => theme.palette['neutral-550'].main }}
                                        fontSize="small"
                                    />
                                    <Typography fontSize={16} color="neutral-550.main" lineHeight="24px" noWrap>
                                        {getEventTypeShortLabel(data.type)}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </>
                )}
            </Stack>
        </Stack>
    );

    if (loading || areLanguageOptionsLoading || !url?.href) {
        return CardContent;
    }

    return (
        <ButtonBase component={Link} to={url.href} focusRipple className="w-full h-full rounded-lg">
            {CardContent}
        </ButtonBase>
    );
};
